import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import type { ColumnState } from '../hooks/use-visible-columns';
import { Checkbox } from './forms';
import { SortIndicator } from './tanstack-table/sort-indicator';

export function SortableColumnRow({
  column,
  sort,
  onToggle,
}: {
  column: ColumnState;
  /** Sort mark to show when this column is part of the active table sort. */
  sort?: { direction: 'asc' | 'desc'; order?: number };
  onToggle: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex flex-row items-center gap-2 p-1 rounded ${
        isDragging ? 'opacity-50 bg-gray-100' : ''
      } ${!column.enabled && !column.pinned ? 'opacity-50' : ''}`}
    >
      {column.pinned ? (
        <>
          <LockClosedIcon className="size-4 text-gray-400 shrink-0" />
          <span className="flex-1 text-sm select-none text-gray-900">{column.id}</span>
        </>
      ) : (
        <button
          type="button"
          className="cursor-pointer flex flex-1 items-center gap-2"
          onClick={() => onToggle(column.id)}
        >
          <Checkbox checked={column.enabled} readOnly className="shrink-0 pointer-events-none" />
          <span
            className={`text-sm select-none ${!column.enabled ? 'text-gray-400' : 'text-gray-900'}`}
          >
            {column.id}
          </span>
        </button>
      )}

      {sort && <SortIndicator direction={sort.direction} order={sort.order} />}

      <button
        type="button"
        className="cursor-grab touch-none text-gray-400 hover:text-gray-600 shrink-0"
        {...attributes}
        {...listeners}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="currentColor"
          role="img"
          aria-label="Drag to reorder"
        >
          <circle cx="4" cy="2" r="1.5" />
          <circle cx="10" cy="2" r="1.5" />
          <circle cx="4" cy="7" r="1.5" />
          <circle cx="10" cy="7" r="1.5" />
          <circle cx="4" cy="12" r="1.5" />
          <circle cx="10" cy="12" r="1.5" />
        </svg>
      </button>
    </div>
  );
}
